import { useState } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { getEquipment, deleteItem } from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation, Tabs, Button } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import useTabs from '@/hooks/useTabs'
import { getStatusBadgeVariant, formatDateTime } from '../../utils'
import { tabs } from './tabs'
import EquipmentComponents from './EquipmentComponents'
import { Trash2 } from 'lucide-react'

const EquipmentDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const { data: equipment, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_EQUIPMENT, id],
        queryFn: () => getEquipment(id!),
    })

    // Delete mutation
    const { mutate: remove, isPending: isDeleting } = useMutationWithHandler(
        {
            mutationFn: () => deleteItem(id!),
            onSuccess: () => {
                setDeleteDialogOpen(false)
                navigate(paths.warehouseItems)
            },
        },
        {
            successMessage: 'Equipment deleted',
            errorMessage: 'Failed to delete equipment',
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_ITEMS,
        }
    )

    const { TabComponent, TabProps } = useTabs({ tabs: tabs(equipment) })

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div>
            <SubHeaders
                actions={[
                    {
                        label: 'Delete',
                        icon: <Trash2 size={16} />,
                        onClick: () => setDeleteDialogOpen(true),
                        variant: 'outlined',
                        color: 'error',
                        disabled: isDeleting,
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: `${paths.warehouseItems}?tab=${equipment?.category || 'server'}`,
                        title: 'Items',
                    },
                    {
                        path: '',
                        title: equipment?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Equipment Name',
                        value: equipment?.name || '-',
                    },
                    {
                        label: 'Service Tag',
                        value: equipment?.service_tag || '-',
                    },
                    {
                        label: 'Serial Number',
                        value: equipment?.serial_number || '-',
                    },
                    {
                        label: 'Ref',
                        value: equipment?.ref || '-',
                        hidden: !equipment?.ref,
                    },
                    {
                        label: 'Category',
                        value: equipment?.category || '-',
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={getStatusBadgeVariant(
                                    equipment?.status || ''
                                )}
                            >
                                {equipment?.status?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Condition',
                        value: equipment?.condition || '-',
                    },
                    {
                        label: 'Location',
                        value: equipment?.current_location?.name || '-',
                        hidden: !equipment?.current_location,
                    },
                    {
                        label: 'Rack',
                        value:
                            equipment?.rack?.name ||
                            equipment?.rack?.code ||
                            '-',
                        hidden: !equipment?.rack,
                    },
                    {
                        label: 'Position',
                        value: equipment?.rack_position_u
                            ? `U${equipment.rack_position_u}`
                            : '-',
                    },
                    {
                        label: 'Model',
                        value: equipment?.model?.name || '-',
                        hidden: !equipment?.model,
                    },
                    {
                        label: 'Supplier',
                        value: equipment?.supplier?.name || '-',
                        hidden: !equipment?.supplier,
                    },
                    {
                        label: 'Purchase Date',
                        value: equipment?.purchase_date
                            ? formatDateTime(equipment.purchase_date)
                            : '-',
                        hidden: !equipment?.purchase_date,
                    },
                    {
                        label: 'Purchase Price',
                        value: equipment?.purchase_price
                            ? `${equipment.purchase_price.toLocaleString()} ${equipment.purchase_currency || 'VND'}`
                            : '-',
                        hidden: !equipment?.purchase_price,
                    },
                    {
                        label: 'Owner',
                        value: equipment?.owner || '-',
                        hidden: !equipment?.owner,
                    },
                    {
                        label: 'Description',
                        value: equipment?.description || '-',
                        className: 'lg:col-span-2',
                        hidden: !equipment?.description,
                    },
                    {
                        label: 'Notes',
                        value: equipment?.notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !equipment?.notes,
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(equipment?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(equipment?.updated_at),
                    },
                ]}
                head={{
                    title: 'Equipment Information',
                }}
            />

            <EquipmentComponents />

            <Tabs {...TabProps} />

            {TabComponent}

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onOpenChange={(open) => setDeleteDialogOpen(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Equipment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this equipment? This
                            will also remove all child components.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outlined"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => remove()}
                            disabled={isDeleting}
                        >
                            Delete Equipment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default EquipmentDetail
